
import { useState } from 'react';

const Notes = () => {
  const [selectedNote, setSelectedNote] = useState('1');
  const [isCreating, setIsCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const notes = [
    {
      id: '1',
      title: 'Initial Client Meeting Notes',
      content: 'Met with TechCorp team to discuss project requirements. Key points:\n\n• Current website is outdated and not mobile-friendly\n• Need modern design that reflects their tech-forward brand\n• Priority on user experience and conversion optimization\n• Timeline is tight - need to launch before Q2\n• Budget approved for additional features if needed\n\nNext steps:\n- Send proposal by Friday\n- Schedule design review meeting\n- Gather brand assets and content',
      category: 'Meeting',
      createdAt: '2024-01-15T10:30:00',
      updatedAt: '2024-01-15T10:30:00',
      author: 'Sarah Johnson',
      tags: ['client-meeting', 'requirements', 'timeline']
    },
    {
      id: '2',
      title: 'Technical Architecture Decisions',
      content: 'Discussed technical approach with development team:\n\n• React + TypeScript for frontend\n• Node.js backend with Express\n• PostgreSQL database\n• AWS hosting with CloudFront CDN\n• Stripe integration for payments\n• Supabase for authentication\n\nPerformance requirements:\n- Page load time < 3 seconds\n- Mobile-first responsive design\n- SEO optimization\n- Accessibility compliance (WCAG 2.1)\n\nSecurity considerations:\n- SSL certificates\n- Data encryption\n- Regular security audits',
      category: 'Technical',
      createdAt: '2024-01-18T14:15:00',
      updatedAt: '2024-01-20T09:45:00',
      author: 'Mike Rodriguez',
      tags: ['architecture', 'technical', 'security']
    },
    {
      id: '3',
      title: 'Design Review Feedback',
      content: 'Client feedback from design presentation:\n\nPositive feedback:\n• Love the modern, clean aesthetic\n• Color scheme works well with brand\n• Navigation is intuitive\n• Mobile design looks great\n\nRequested changes:\n• Make hero section more prominent\n• Add testimonials section\n• Adjust font sizes for better readability\n• Include more call-to-action buttons\n• Consider adding a blog section\n\nRevisions needed:\n- Update homepage mockup\n- Create testimonials component\n- Adjust typography scale\n- Add blog page design',
      category: 'Design',
      createdAt: '2024-01-22T16:20:00',
      updatedAt: '2024-01-22T16:20:00',
      author: 'Lisa Kim',
      tags: ['design-review', 'feedback', 'revisions']
    },
    {
      id: '4',
      title: 'Weekly Team Standup - Week 2',
      content: 'Team progress update:\n\nSarah (PM):\n• Client approved design revisions\n• Scheduled development kickoff\n• Updated project timeline\n\nMike (Dev):\n• Set up development environment\n• Created initial project structure\n• Started homepage component development\n\nLisa (Design):\n• Completed design system documentation\n• Created component library\n• Working on internal page designs\n\nBlockers:\n• Waiting for final content from client\n• Need access to their analytics data\n\nNext week goals:\n• Complete homepage development\n• Finalize content strategy\n• Begin testing phase',
      category: 'Meeting',
      createdAt: '2024-01-26T09:00:00',
      updatedAt: '2024-01-26T09:00:00',
      author: 'Sarah Johnson',
      tags: ['standup', 'progress', 'team-update']
    }
  ];

  const categories = ['All', 'Meeting', 'Technical', 'Design', 'Personal'];

  const filteredNotes = notes.filter(note => 
    note.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    note.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
    note.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const currentNote = notes.find(note => note.id === selectedNote);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      'Meeting': 'bg-blue-100 text-blue-800',
      'Technical': 'bg-green-100 text-green-800',
      'Design': 'bg-purple-100 text-purple-800',
      'Personal': 'bg-yellow-100 text-yellow-800'
    };
    return colors[category as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Project Notes</h3>
        <button
          onClick={() => setIsCreating(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap"
        >
          <i className="ri-add-line mr-2"></i>
          New Note
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Notes List */}
        <div className="lg:col-span-1 space-y-4">
          {/* Search */}
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
            <input
              type="text"
              placeholder="Search notes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-1">
            {categories.map((category) => (
              <button
                key={category}
                className="px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors whitespace-nowrap"
              >
                {category}
              </button>
            ))}
          </div>

          {/* Notes List */}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredNotes.map((note) => (
              <div
                key={note.id}
                onClick={() => setSelectedNote(note.id)}
                className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedNote === note.id
                    ? 'border-blue-200 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium text-gray-900 text-sm line-clamp-2">{note.title}</h4>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full flex-shrink-0 ml-2 ${getCategoryColor(note.category)}`}>
                    {note.category}
                  </span>
                </div>
                <p className="text-xs text-gray-600 line-clamp-2 mb-2">{note.content}</p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>{note.author}</span>
                  <span>{formatDate(note.createdAt)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Note Content */}
        <div className="lg:col-span-2">
          {currentNote ? (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">{currentNote.title}</h2>
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span>By {currentNote.author}</span>
                    <span>Created {formatDate(currentNote.createdAt)}</span>
                    {currentNote.updatedAt !== currentNote.createdAt && (
                      <span>Updated {formatDate(currentNote.updatedAt)}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(currentNote.category)}`}>
                    {currentNote.category}
                  </span>
                  <button className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100">
                    <i className="ri-edit-line"></i>
                  </button>
                  <button className="p-2 text-gray-400 hover:text-red-600 rounded-md hover:bg-gray-100">
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              </div>

              <div className="prose max-w-none">
                <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                  {currentNote.content}
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Tags:</span>
                  <div className="flex flex-wrap gap-1">
                    {currentNote.tags.map((tag, index) => (
                      <span key={index} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md">
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
              <i className="ri-sticky-note-line text-4xl text-gray-300 mb-4"></i>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select a note to view</h3>
              <p className="text-gray-500">Choose a note from the list to view its content</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Note Modal */}
      {isCreating && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Create New Note</h3>
                <button
                  onClick={() => setIsCreating(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter note title..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-8">
                  <option value="Meeting">Meeting</option>
                  <option value="Technical">Technical</option>
                  <option value="Design">Design</option>
                  <option value="Personal">Personal</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                <textarea
                  rows={12}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Write your note content here..."
                ></textarea>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter tags separated by commas..."
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex items-center justify-end space-x-3">
              <button
                onClick={() => setIsCreating(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={() => setIsCreating(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors whitespace-nowrap"
              >
                Create Note
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notes;
